import { useContext, useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import {
  ComputerDesktopIcon,
  WrenchIcon,
  HomeIcon,
  GlobeEuropeAfricaIcon,
} from '@heroicons/react/24/outline';
import { UserCircleIcon } from '@heroicons/react/24/solid';
import { AppContext } from '@rabbit/app-context';
import {
  ButtonIconVertical,
  IWarrantyLogoIcon,
  useTenantBrandIdentity,
} from '@rabbit/elements/shared-components';
import ROUTE_NAME from '../../../utils/url-constants';

/* eslint-disable-next-line */
export interface NavBarProps {}

const navBarOptions = [
  { id: '1', path: ROUTE_NAME.DASHBOARD, label: 'Home', icon: HomeIcon },
  {
    id: '2',
    path: ROUTE_NAME.PRODUCTS,
    label: 'Products',
    icon: ComputerDesktopIcon,
  },
  { id: '3', path: ROUTE_NAME.REPAIRS, label: 'Repairs', icon: WrenchIcon },
  {
    id: '4',
    path: ROUTE_NAME.IMPACT,
    label: 'Impact',
    icon: GlobeEuropeAfricaIcon,
  },
];

const dynamicStyles = {
  header:
    'z-30 fixed left-0 bottom-0 bg-gray-50 w-full md:relative md:bottom-auto md:bg-white border-b',
};

export function NavBar(props: NavBarProps) {
  const navigate = useNavigate();
  const { tenantInfo } = useContext(AppContext);

  // Hide when ?tab=chat
  const [hide, setHide] = useState(false);
  const [paramValue, setParamValue] = useState<string>('');
  const QUERY_PARAM = 'tab';
  const searchParams = new URLSearchParams(window.location.search);

  useEffect(() => {
    const currentValue = searchParams.get(QUERY_PARAM) || '';
    if (currentValue !== paramValue) {
      setParamValue(currentValue);
      if (currentValue === 'chat') setHide(true);
      else setHide(false);
    }
  }, [window.location.search, QUERY_PARAM, paramValue]);

  // Branding
  const brandFromQuery = searchParams.get('brand');
  const { tenantBrandLogo, tenantBrandName } = useTenantBrandIdentity({
    tenantInfo,
    brand: brandFromQuery || undefined,
  });
  const displayLogo = tenantBrandLogo || tenantInfo?.logo;
  const displayName = tenantBrandName || tenantInfo?.name;

  // Brand Logo
  const BrandLogo = () => (
    <NavLink to="/" className="inline-flex items-center gap-4">
      <IWarrantyLogoIcon className="h-10 w-auto" />
      <span className="h-10 border-r-2 border-gray-300" />
      <img src={displayLogo} alt={displayName} className="h-8" />
    </NavLink>
  );

  return (
    <>
      <div className="flex items-center border-b bg-white p-4 md:hidden">
        <BrandLogo />
      </div>

      <header
        className={classNames(dynamicStyles.header, {
          ['hidden md:block']: hide,
        })}
      >
        <div className="flex w-full items-center gap-4 py-2 px-4 md:mx-auto md:max-w-5xl md:py-6">
          {/* Logo */}
          <div className="hidden md:inline-flex md:flex-shrink-0">
            <BrandLogo />
          </div>

          {/* Menu */}
          <div className="flex w-full justify-around gap-2 md:mx-auto md:w-auto md:gap-8 lg:gap-10">
            {navBarOptions.map((item, index) => (
              <NavLink to={item.path} className="no-underline" key={index}>
                {({ isActive }) => {
                  return (
                    <ButtonIconVertical
                      kind="navbar"
                      Icon={item.icon}
                      label={item.label}
                      isActive={isActive}
                    />
                  );
                }}
              </NavLink>
            ))}

            <NavLink
              to={ROUTE_NAME.ACCOUNT}
              className="block no-underline md:hidden"
            >
              {({ isActive }) => {
                return (
                  <ButtonIconVertical
                    kind="navbar"
                    Icon={UserCircleIcon}
                    label="Account"
                    isActive={isActive}
                  />
                );
              }}
            </NavLink>
          </div>

          <button
            className="hidden cursor-pointer md:inline-flex md:flex-shrink-0"
            onClick={() => navigate(`${ROUTE_NAME.ACCOUNT}`)}
          >
            <UserCircleIcon className="text-primary-900 h-8 w-8" />
          </button>
        </div>
      </header>
    </>
  );
}

export default NavBar;
